/**
 * Receipts — money in, by any mode (spec §G4, ADR-0011 §6). Redrawn on the
 * list shell in Wave 15D (C14 §10.6 items 1–2, ADR-0033; T-6.16): search,
 * mode / status / client / year chips, sortable columns, paging and Export.
 * The two dialogs moved to their own files — `RecordReceiptDialog` (the six
 * manual modes, oldest-first application) and `RefundDialog` (FR-E13's split,
 * the gateway, the step-up) — where their reasoning is written down.
 *
 * Anything left over from a receipt is an **advance**, said out loud on the
 * row afterwards rather than left as a silent difference between two numbers.
 *
 * **The refund lives here, not on the invoice** — FR-E13, ADR-0011 §8. It is a
 * row action, shown only to somebody who holds the firm's refund approver role
 * (`can_refund` is the server's own answer, using the rule the route enforces)
 * and never while a refund already waits on the gateway, which the server
 * refuses a second of.
 *
 * **`?client_id=` narrows the list to one client** — C27 (spec §10.19 item 7,
 * FR-E13; UAT report #3). A case's *Refund…* opens this screen for its client:
 * the refund is raised from the case and executed here, on the receipt, with
 * the same row action and the same approver. The narrowing is the server's
 * (`GET /billing/receipts?client_id=`), so a client with more receipts than one
 * page still shows all of theirs, and it is said out loud — *Showing receipts
 * for …* — with a way back to every receipt.
 */

import { Alert, Button } from '@fiveox/ui'
import { Plus, Undo2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'

import {
  ExportButton,
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  StatusBadge,
  statusTone,
  useListTable,
} from '../../components/page'
import { LEDGER, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type BillingVocabulary,
  MODE_LABELS,
  type Receipt,
  type ReceiptMode,
  billing,
  receiptFlags,
  rupees,
} from '../../lib/api/billing'
import { RecordReceiptDialog } from './RecordReceiptDialog'
import { RefundDialog } from './RefundDialog'
import { Money, activeClients, clientFilter, namesOf, yearFilter } from './shared'

/** The most the receipts API returns in one read (`le=200`). */
const RECEIPT_PAGE = 200

/** A receipt's states, as the status map names them (`statusTone('receipt')`),
 *  each with the words it is shown in. The gateway's facts come first and keep
 *  `receiptFlags`' own sentences (they carry the amount); a receipt with none
 *  is *applied* or has an *advance held*. Read off the server's fields —
 *  nothing here decides whether a flag applies. */
export function receiptStates(receipt: Receipt): Array<{ status: string; label: string }> {
  const flags = receiptFlags(receipt)
  const states: Array<{ status: string; label: string }> = []
  if (receipt.refund_requested) states.push({ status: 'refund_requested', label: flags[0]!.text })
  if (receipt.disputed_at) {
    states.push({ status: 'disputed', label: flags.find((f) => f.text.startsWith('Disputed'))!.text })
  }
  if (receipt.overpaid_amount) {
    states.push({ status: 'overpaid', label: flags.find((f) => f.text.startsWith('Overpaid'))!.text })
  }
  if (states.length === 0) {
    states.push(
      receipt.unapplied_amount === '0.00'
        ? { status: 'applied', label: statusTone('receipt', 'applied').label }
        : { status: 'advance', label: statusTone('receipt', 'advance').label },
    )
  }
  return states
}

interface ReceiptRow extends Receipt {
  client_name: string
  states: Array<{ status: string; label: string }>
}

const STATUS_CHIPS = ['applied', 'advance', 'refund_requested', 'disputed', 'overpaid'] as const

export function Receipts() {
  const { can } = useEvaluation()
  const [params, setParams] = useSearchParams()
  const onlyClient = params.get('client_id') || null
  const [receipts, setReceipts] = useState<Receipt[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [names, setNames] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [recording, setRecording] = useState(false)
  const [vocab, setVocab] = useState<BillingVocabulary | null>(null)
  const [refunding, setRefunding] = useState<Receipt | null>(null)

  const load = useCallback(async () => {
    try {
      const [list, book, v] = await Promise.all([
        billing.receipts({ limit: RECEIPT_PAGE, client_id: onlyClient ?? undefined }),
        activeClients(),
        billing.vocabulary(),
      ])
      setReceipts(list.data)
      const counted = list.meta?.total
      setTotal(typeof counted === 'number' ? counted : null)
      setNames(namesOf(book))
      setVocab(v.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The receipts could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [onlyClient])

  useEffect(() => {
    void load()
  }, [load])

  const rows = useMemo<ReceiptRow[]>(
    () =>
      receipts.map((receipt) => ({
        ...receipt,
        client_name: names[receipt.client_id] ?? '—',
        states: receiptStates(receipt),
      })),
    [receipts, names],
  )

  const columns = useMemo<ListColumn<ReceiptRow>[]>(
    () => [
      {
        key: 'number',
        label: 'Receipt',
        width: 150,
        required: true,
        phone: true,
        sortField: 'number',
        render: (row) => (
          <span className="block min-w-0">
            <span className="font-medium whitespace-nowrap">{row.number}</span>
            {/* On a phone the other columns are gone — the Refund action keeps
                its place at the right edge — so the client, the amount and the
                state ride here. */}
            <span className="text-muted-foreground block max-w-[13rem] truncate text-xs sm:hidden">
              {row.client_name} · {rupees(row.amount)}
            </span>
            <span className="mt-1 flex max-w-[13rem] flex-wrap gap-1 sm:hidden">
              {row.states.map((state) => (
                <StatusBadge key={state.status} kind="receipt" status={state.status} label={state.label} />
              ))}
            </span>
          </span>
        ),
        exportValue: (row) => row.number,
      },
      {
        key: 'client',
        label: 'Client',
        width: 150,
        sortField: 'client_name',
        render: (row) => (
          <span className="block max-w-[9rem] truncate sm:max-w-[7.5rem]" title={row.client_name}>
            {row.client_name}
          </span>
        ),
        exportValue: (row) => row.client_name,
      },
      {
        key: 'mode',
        label: 'Mode',
        width: 110,
        sortField: 'mode',
        render: (row) => (
          <span className="block">
            {MODE_LABELS[row.mode]}
            {row.reference && (
              <span className="text-muted-foreground block text-xs">{row.reference}</span>
            )}
          </span>
        ),
        exportValue: (row) => MODE_LABELS[row.mode],
      },
      {
        key: 'reference',
        label: 'Reference',
        width: 0,
        // In the sheet only: on screen it sits under the mode.
        render: () => null,
        exportValue: (row) => row.reference ?? '',
      },
      {
        key: 'received_on',
        label: 'Received',
        width: 100,
        sortField: 'received_on',
        render: (row) => (
          <span className="text-muted-foreground whitespace-nowrap">{row.received_on}</span>
        ),
        exportValue: (row) => row.received_on,
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 100,
        align: 'right',
        sortField: 'amount',
        render: (row) => <Money value={row.amount} />,
        exportValue: (row) => row.amount,
      },
      {
        key: 'unapplied_amount',
        label: 'Held as advance',
        width: 110,
        align: 'right',
        sortField: 'unapplied_amount',
        render: (row) => (row.unapplied_amount === '0.00' ? '—' : <Money value={row.unapplied_amount} />),
        exportValue: (row) => row.unapplied_amount,
      },
      {
        key: 'status',
        label: 'Status',
        width: 190,
        render: (row) => (
          <span className="flex flex-wrap gap-1">
            {row.states.map((state) => (
              <StatusBadge key={state.status} kind="receipt" status={state.status} label={state.label} />
            ))}
          </span>
        ),
        exportValue: (row) => row.states.map((s) => s.label).join('; '),
      },
    ],
    [],
  )

  // What the table draws, from the columns the export carries — the export
  // keeps `columns`: its order, its eight columns, the export-only Reference.
  // At 1280 beside the rail the seven drawn columns ran under the sticky
  // Refund, Status first among them. So the screen puts Status after the
  // client, lets its badges wrap, truncates the client's name (the whole of it
  // in a tooltip) and folds two columns into a second line: the mode and its
  // reference under the receipt's number (the Mode chip still narrows by it),
  // and what is held as an advance under the amount (as does *Advance held*).
  const shown = useMemo<ListColumn<ReceiptRow>[]>(() => {
    const pick = (key: string) => {
      const found = columns.find((c) => c.key === key)
      if (!found) throw new Error(`no receipt column ${key}`)
      return found
    }
    const number = pick('number')
    return [
      {
        ...number,
        render: (row) => (
          <span className="block min-w-0">
            {number.render(row)}
            <span className="text-muted-foreground hidden max-w-[11rem] truncate text-xs sm:block">
              {MODE_LABELS[row.mode]}
              {row.reference && <span title={row.reference}> · {row.reference}</span>}
            </span>
          </span>
        ),
      },
      { ...pick('client'), width: 140 },
      {
        ...pick('status'),
        width: 180,
        render: (row) => (
          <span className="flex max-w-[11rem] flex-wrap gap-1">
            {row.states.map((state) => (
              <StatusBadge key={state.status} kind="receipt" status={state.status} label={state.label} />
            ))}
          </span>
        ),
      },
      {
        ...pick('amount'),
        width: 120,
        render: (row) => (
          <span className="block">
            <Money value={row.amount} />
            {row.unapplied_amount !== '0.00' && (
              <span className="text-muted-foreground block text-xs whitespace-nowrap">
                {rupees(row.unapplied_amount)} held
              </span>
            )}
          </span>
        ),
      },
      { ...pick('received_on'), width: 100 },
    ]
  }, [columns])

  const modes = [...new Set(receipts.map((r) => r.mode))] as ReceiptMode[]
  const list = useListTable(rows, {
    columns: shown,
    search: (row) => [row.number, row.client_name, row.reference, MODE_LABELS[row.mode]],
    filters: [
      {
        key: 'mode',
        label: 'Mode',
        allLabel: 'Every mode',
        options: modes.map((mode) => ({ value: mode, label: MODE_LABELS[mode] })),
        match: (row, value) => row.mode === value,
      },
      {
        key: 'status',
        label: 'Status',
        allLabel: 'Any status',
        options: STATUS_CHIPS.map((status) => ({
          value: status,
          label: statusTone('receipt', status).label,
        })),
        match: (row, value) => row.states.some((s) => s.status === value),
      },
      clientFilter(rows, names),
      yearFilter(rows),
    ],
  })

  const clearClient = () => {
    const next = new URLSearchParams(params)
    next.delete('client_id')
    setParams(next)
  }
  const onlyName = onlyClient ? (names[onlyClient] ?? 'this client') : null

  const canRecord = can(LEDGER, 'can_create')
  const record = canRecord ? (
    <Button onClick={() => setRecording(true)}>
      <Plus size={16} aria-hidden /> Record a receipt
    </Button>
  ) : undefined

  return (
    <div className="space-y-6">
      <ListPage
        title="Receipts"
        description={`Money in, however it arrived. Every mode records the same way.${
          total !== null && total > receipts.length
            ? ` Showing your latest ${receipts.length} of ${total}.`
            : ''
        }`}
        primary={record}
        loading={loading}
        error={error}
        isEmpty={list.isEmpty}
        empty={
          onlyClient
            ? {
                // Narrowed by a case's *Refund…*: nothing to refund yet, and
                // the way back to every receipt rather than a dead end.
                title: `No receipts for ${onlyName}`,
                description:
                  'Nothing has been received from this client yet, so there is nothing to refund.',
                action: (
                  <Button variant="outline" onClick={clearClient}>
                    Show every receipt
                  </Button>
                ),
              }
            : {
                title: 'Nothing recorded yet',
                description:
                  'Record a cheque, cash, a bank transfer or a UPI payment here. It is applied to the client’s oldest invoice first, and anything left over is held as an advance.',
                action: record,
              }
        }
        toolbar={
          <ListToolbar
            list={list}
            placeholder="Search by number, client or reference"
            actions={
              <ExportButton rows={list.sorted} columns={columns} filename="prodesk-receipts" />
            }
          />
        }
      >
        {notice && <Alert tone="success">{notice}</Alert>}
        {onlyClient && (
          <div
            className="border-border bg-muted/40 inline-flex items-center gap-2 rounded-full border py-1 pr-1 pl-3 text-sm"
            data-client-chip
          >
            <span>
              Showing receipts for{' '}
              <strong className="font-medium">{onlyName}</strong>
            </span>
            <Button variant="ghost" size="sm" onClick={clearClient}>
              <X size={14} aria-hidden /> Show every receipt
            </Button>
          </div>
        )}
        <ListTable
          list={list}
          rowKey={(row) => row.id}
          trailing={
            vocab?.can_refund
              ? {
                  header: <span className="sr-only">Refund</span>,
                  width: 100,
                  // One refund waits on the gateway at a time; the server
                  // refuses a second, so the button is not offered while the
                  // first reads "refund requested".
                  cell: (row) =>
                    row.refund_requested ? null : (
                      <Button variant="ghost" size="sm" onClick={() => setRefunding(row)}>
                        <Undo2 size={14} aria-hidden /> Refund
                      </Button>
                    ),
                }
              : undefined
          }
        />
      </ListPage>

      {refunding && (
        <RefundDialog
          receipt={refunding}
          vocab={vocab}
          onClose={() => setRefunding(null)}
          onRefunded={(message) => {
            setRefunding(null)
            setNotice(message)
            void load()
          }}
        />
      )}

      {recording && (
        <RecordReceiptDialog
          onClose={() => setRecording(false)}
          onRecorded={(receipt) => {
            setRecording(false)
            setNotice(
              receipt.unapplied_amount === '0.00'
                ? `${receipt.number} recorded and applied.`
                : `${receipt.number} recorded — ${rupees(receipt.unapplied_amount)} is held as an advance.`,
            )
            void load()
          }}
        />
      )}
    </div>
  )
}
